import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { clearState } from 'app/app.actions';
import { AclService } from 'app/shared/acl/acl.service';
import { asyncScheduler, filter, observeOn, of, take } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { AuthApi } from './auth-api.service';
import {
    invalidToken,
    login,
    logout,
    refreshBranding,
    refreshCredentials,
    refreshUser,
    tokenNotPresent,
    updateBranding,
    updateUser,
    userUpdated
} from './auth.actions';


declare let ga: (...args: any[]) => void;

@Injectable()
export class AuthEffects {

    loggedIn = createEffect(() => this.actions.pipe(
        ofType(login),
        tap(_ => {
            const url = this.router.routerState.snapshot.root.queryParams.redirect || '/';
            this.router.navigateByUrl(url);
        })
    ), { dispatch: false });

    loggedOut = createEffect(() => this.actions.pipe(
        ofType(logout, invalidToken),
        tap(() => {
            if (!this.router.routerState.snapshot.url.startsWith('/forgot-password')) {
                // ignore navigation when on forgot password page
                this.router.navigate(['login']);
            }
        })
    ), { dispatch: false });

    logoutToGa = createEffect(() => this.actions.pipe(
        ofType(logout),
        tap(() => {
            if (typeof ga !== 'undefined') {
                ga('send', 'event', 'user', 'logout', 'logout');
            }
        })
    ), { dispatch: false });

    refreshUser = createEffect(() => this.actions.pipe(
        ofType(refreshUser),
        switchMap(() => this.authApi.refreshUser()),
        map(response => updateUser({
            user: response.payload.account,
            resources: response.payload.allowedResources,
            disclaimers: response.payload.disclaimers,
        }))
    ));

    refreshCredentials = createEffect(() => this.actions.pipe(
        ofType(refreshCredentials),
        switchMap(() => this.authApi.refreshCredentials()),
        map(response => userUpdated({ user: { filledCredentials: response.payload.filledCredentials } }))
    ));

    refreshBranding = createEffect(() => this.actions.pipe(
        ofType(refreshBranding),
        switchMap(() => this.authApi.refreshBranding()),
        map(response => updateBranding({ branding: response.payload.branding }))
    ));

    userUpdated = createEffect(() => this.actions.pipe(
        ofType(updateUser),
        tap(({ user, resources }) => {
            if (user?.username) {
                this.authApi.setCustomHeader('APUser', user.username);
                console.log('User: ', user.username);
            }
            if (user?.uid) {
                this.authApi.setCustomHeader('X-Amzn-Trace-Id', `APUser=${user.uid}`);
            }
            if (resources) {
                this.aclService.configure(resources);
                console.log('Resources: ', resources);
            }
        }),
        map(payload => userUpdated(payload)),
    ));

    refreshGuards = createEffect(() => this.actions.pipe(
        ofType(userUpdated),
        observeOn(asyncScheduler),
        switchMap(() => {
            if (this.router.navigated && !this.router.currentNavigation()) {
                return of(this.router.url);
            } else {
                return this.router.events.pipe(
                    filter(e => e instanceof NavigationEnd),
                    map(e => e.urlAfterRedirects),
                    take(1),
                );
            }
        }),
        tap(url => this.router.navigateByUrl(url, { onSameUrlNavigation: 'reload' }))
    ), { dispatch: false });

    clearState = createEffect(() => this.actions.pipe(
        ofType(logout, invalidToken, tokenNotPresent),
        map(() => clearState())
    ));

    constructor(
        private actions: Actions,
        private router: Router,
        private authApi: AuthApi,
        private aclService: AclService
    ) {}
}
